/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import CaptureHeader from '../CaptureHeader';
import ReferenceTooltip from './ReferenceTooltip';
import { DEFAULT_CUSTOMER_REFERENCE_LABEL, resolveCustomerReferenceLabel } from '../../utils/customerReferenceLabel';

import './capture.css';

const Done = ({
  assignment,
  items,
  tenant,
  branding,
  workflow,
}) => {
  const currentTheme = branding.theme || 'light';
  const [showTooltip, setShowTooltip] = useState(false);
  const [showNameTooltip, setShowNameTooltip] = useState(false);
  const tooltipTriggerRef = useRef(null);
  const nameTooltipTriggerRef = useRef(null);
  const dynamicCustomerReferenceLabel = resolveCustomerReferenceLabel(workflow);

  // Count completed items by type (identifier counts as image)
  const completedItems = items.filter(i => i.status === 'completed');
  const imageCount = completedItems.filter(i => i.type === 'photo' || i.type === 'identifier').length;
  const videoCount = completedItems.filter(i => i.type === 'video').length;
  const fileCount = completedItems.filter(i => i.type === 'file').length;

  const thankyouTitle = workflow?.branding?.thankyouTitle;
  const thankyouMessage = workflow?.branding?.thankyouMessage;
  const whatHappensNextDescription = workflow.branding?.whatHappensNextDescription;

  return (
    <div data-theme={currentTheme} className="min-h-screen flex flex-col bg-base-100 text-base-content overflow-x-hidden capture-container">
      {/* Notch Spacer */}
      <div className="h-[env(safe-area-inset-top,20px)] bg-base-100" />

      {/* Header */}
      <div className="w-full max-w-lg mx-auto px-3">
        <CaptureHeader expiresAt={assignment?.expiresAt} brandLogo={workflow.branding?.logoUrl} branding={branding} type='done' />
      </div>

      <div className="flex-1 flex flex-col items-start px-3 max-w-lg mx-auto w-full pt-2">
        <div className="w-full mb-8 flex flex-col gap-1">
          <div className="relative min-w-0">
            <h2
              ref={nameTooltipTriggerRef}
              className="text-base-content text-[20px] font-bold tracking-tighter leading-none truncate cursor-pointer transition-opacity active:opacity-70 hover:opacity-80"
              onClick={() => setShowNameTooltip(!showNameTooltip)}
            >
              {assignment.submitterName || assignment.workflowName || 'Mobile Capture'}
            </h2>

            <ReferenceTooltip
              assignment={assignment}
              workflow={workflow}
              show={showNameTooltip}
              onClose={() => setShowNameTooltip(false)}
              theme={currentTheme}
              triggerRef={nameTooltipTriggerRef}
              type="name"
            />
          </div>
          <div className="text-[14px] text-base-content leading-tight shrink-0">
            <div className="flex justify-between items-start overflow-visible custom-line-break w-full">
              <span>{dynamicCustomerReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicCustomerReferenceLabel}:{" "}</span>
              <span className="inline-block text-right shrink-0">
                <button
                  type="button"
                  ref={tooltipTriggerRef}
                  onClick={() => setShowTooltip(!showTooltip)}
                  className="text-primary underline decoration-dashed focus:outline-none cursor-pointer relative z-[30] custom-line-break"
                >
                  {assignment.customerReferenceNumber}
                </button>
              </span>
              <ReferenceTooltip
                assignment={assignment}
                workflow={workflow}
                show={showTooltip}
                onClose={() => setShowTooltip(false)}
                theme={currentTheme}
                triggerRef={tooltipTriggerRef}
              />
            </div>
          </div>
        </div>
        {/* Success Graphic */}
        {/* <div className="w-full flex justify-center mb-12">
          <img src={submittedIcon} alt="Success" className="w-[90px] h-auto object-contain" />
        </div> */}

        <h1 className="text-[24px] font-bold text-base-content mb-1 tracking-tight custom-line-break">{(thankyouTitle || 'Media submitted!').replaceAll('{name}', assignment.submitterName || '')}</h1>
        <p className="text-base-content text-[16px] mb-1 custom-line-break">{thankyouMessage || 'Your items have been securely sent.'}</p>

        {/* ITEMS SUBMITTED */}
        <div className="w-full mb-12 mt-8 flex gap-3">
          <div className="flex-1 bg-[#D9D9D9] rounded-[8px] p-3 flex flex-col items-center justify-center">
            <span className="text-[16px] font-normal text-black">Images</span>
            <span className="text-[40px] font-normal text-black mt-[5px] leading-none">{imageCount}</span>
          </div>
          <div className="flex-1 bg-[#D9D9D9] rounded-[8px] p-3 flex flex-col items-center justify-center">
            <span className="text-[16px] font-normal text-black">Files</span>
            <span className="text-[40px] font-normal text-black mt-[5px] leading-none">{fileCount}</span>
          </div>
          <div className="flex-1 bg-[#D9D9D9] rounded-[8px] p-3 flex flex-col items-center justify-center">
            <span className="text-[16px] font-normal text-black">Videos</span>
            <span className="text-[40px] font-normal text-black mt-[5px] leading-none">{videoCount}</span>
          </div>
        </div>

        {/* WHAT HAPPENS NEXT */}
        <div className="w-full text-left mb-6 pt-4">
          <h3 className="text-[18px] font-bold text-base-content tracking-tight mb-3">What happens next</h3>
          <div className="w-full bg-primary/30 rounded-[8px] p-5 border border-primary/5">
            <p className="text-[16px] leading-tight text-base-content custom-line-break">
              {whatHappensNextDescription || 'Your submitted imagery will be reviewed. You will receive updates once the process is complete.'}
            </p>
          </div>
        </div>

        {/* Safe to close text */}
        <div className="w-full text-center mt-8 pb-4">
          <p className="text-[24px] font-normal text-base-content">Safe to close this window</p>
        </div>
      </div>

      {/* Bottom Spacer */}
      <div className="h-[env(safe-area-inset-bottom,40px)] bg-base-100" />
    </div>
  );
};

export default Done;

Done.propTypes = {
  assignment: PropTypes.shape({
    expiresAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    submitterName: PropTypes.string,
    workflowName: PropTypes.string,
    customerReferenceNumber: PropTypes.string,
  }),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.string,
      type: PropTypes.string,
    })
  ),
  tenant: PropTypes.any,
  branding: PropTypes.shape({
    theme: PropTypes.string,
  }),
  workflow: PropTypes.shape({
    branding: PropTypes.shape({
      thankyouTitle: PropTypes.string,
      thankyouMessage: PropTypes.string,
      whatHappensNextDescription: PropTypes.string,
      logoUrl: PropTypes.string,
    }),
  }),
};
